<template>
  <section class="screen game">
    <!-- Contexto e saída, sempre (diretiva 04): de onde é esta partida, quanto já foi, e
         por onde se sai dela. -->
    <div v-if="game" class="context">
      <div class="context-top">
        <span class="lbl">{{ contextLabel }}</span>

        <router-link v-if="gamePhase" class="exit" to="/trilha">
          <AppIcon name="flag" size="sm" />ver a trilha
        </router-link>
        <router-link v-else-if="gameDuel" class="exit" :to="`/duelos/${gameDuel}`">
          <AppIcon name="swords" size="sm" />ver o duelo
        </router-link>
        <button v-else class="exit" type="button" @click="openModes">
          <AppIcon name="layers" size="sm" />trocar de modo
        </button>

        <!-- A ajuda vive aqui desde que o cabeçalho deixou de ter botões: sem este botão,
             a regra do jogo e o tutorial ficaram sem porta na tela onde mais importam. -->
        <button class="help" type="button" aria-label="Como se joga" @click="openHelp">
          <AppIcon name="circle-help" size="sm" />
        </button>
      </div>

      <div class="context-bottom">
        <div class="steps">
          <span v-for="(state, i) in steps" :key="i" class="step" :class="state" />
        </div>
        <span class="num count">{{ resolved }}/{{ steps.length }}</span>

        <button v-if="!isOver" class="give-up" type="button" @click="askGiveUp">
          <AppIcon name="x" size="sm" />desistir
        </button>
      </div>

      <!-- Relógio do duelo: o número desce em toda resposta do servidor, e o desconto
           local entre uma e outra é só desenho. -->
      <div v-if="gameDuel && !atStart && !isOver" class="clock" :class="{ pressed }">
        <AppIcon name="timer" size="sm" />
        <span class="num">{{ clock }}</span>
        <span class="time-bar">
          <span class="bar-fill" :style="{ width: `${timePct}%` }" />
        </span>
      </div>
    </div>

    <!-- A dica é o significado da palavra que o jogador já tem na mão: a âncora, ou a que
         ele acabou de acertar. Não é spoiler (diretiva 03) — é o fio.
         TODO backend: uma dica própria por palavra, escrita pela curadoria, sem citar a
         palavra procurada. Hoje mostramos o significado da anterior. -->
    <div v-if="game && !isOver && currentHint" class="hint">
      <AppIcon name="lightbulb" size="lg" />
      <div>
        <div class="lbl">dica · ligada a {{ previousWord }}</div>
        <p>{{ currentHint }}</p>
      </div>
    </div>

    <div class="chain">
      <template v-if="game">
        <!-- Âncora: dada, e por isso violeta e não verde. -->
        <div class="link link-anchor">
          <AppIcon name="link" size="sm" />
          <span class="word">{{ game.anchor }}</span>
          <span class="lbl">início</span>
        </div>

        <template v-for="word in game.words" :key="word.position">
          <!-- Diretiva 05: palavra resolvida colapsa numa linha de altura única. -->
          <div v-if="word.solved !== null" class="link" :class="word.solved ? 'link-ok' : 'link-error'">
            <AppIcon :name="word.solved ? 'check' : 'x'" size="sm" />
            <span class="word">{{ word.word }}</span>
            <span class="num dot">{{ word.solved ? `+${word.points}` : '0' }}</span>
          </div>

          <!-- A palavra corrente: de onde ela vem, quanto vale agora, e as caixas. -->
          <div v-else-if="word.position === game.current_position && !isOver" class="active">
            <div class="active-top">
              <span class="lbl">
                <AppIcon name="link" size="sm" />se liga a {{ previousWord }}
              </span>
              <!-- Diretiva 07: o valor fica à vista e cai a cada letra revelada. Quem
                   prefere jogar sem placar desliga em Configurações. -->
              <span v-if="preferences.wordPoints" class="value" :class="{ falling: revealedCount > 1 }">
                <AppIcon name="trophy" size="sm" />{{ worth }} pts
              </span>
            </div>

            <div class="boxes" :class="feedbackClass">
              <GameTile
                v-for="(tile, i) in tiles"
                :key="i"
                :char="tile.char"
                :state="tile.state"
                :index="i"
                :dock="dockByPosition[i] || null"
                :selectable="!isOver && i >= revealedCount"
                :selected="tile.selected"
                @select="pickTile(i)"
              />
            </div>
          </div>

          <!-- Diretiva 03: palavra futura é cadeado, interrogação e número de letras. -->
          <div v-else class="future">
            <AppIcon name="lock" size="sm" />
            <span class="question">?</span>
            <span class="dots"><i v-for="n in word.length" :key="n" /></span>
            <span class="letters">{{ word.length }} letras</span>
          </div>
        </template>
      </template>

      <p v-else-if="loading" class="loading lbl">preparando a chain…</p>

      <!-- Sem partida e sem folha aberta: quem fechou a escolha precisa de um caminho de
           volta para ela, senão a tela fica vazia sem dizer por quê. -->
      <div v-else class="no-game">
        <p>Escolha como jogar: a chain do dia, o treino ou um duelo. A trilha fica na barra.</p>
        <button class="btn btn-cta" type="button" @click="modesOpen = true">
          <AppIcon name="gamepad-2" size="sm" />escolher modo
        </button>
      </div>
    </div>

    <StatusLine :message="message" />

    <VirtualKeyboard
      v-if="typing"
      class="keyboard"
      :can-submit="canSubmit"
      :hit-key="hitKey"
      :results="keyboard"
      @press="onPress"
    />

    <DuelCountdown v-if="atStart" :seconds="segundosDaLargada" :limit="duelLimit" />

    <ModeSheet
      v-if="modesOpen"
      :modes="modes"
      :has-result="!!result"
      :model-value="hasGame ? game.mode : ''"
      @choose="chooseMode"
      @result="openStoredResult"
      @close="modesOpen = false"
    />

    <HelpSheet
      v-if="helpOpen"
      :modes="modes"
      @tutorial="replayTutorial"
      @close="helpOpen = false"
    />

    <TutorialSheet v-if="tutorialOpen" :first-time="tutorialAuto" @close="closeTutorial" />

    <!-- Desistir é escolha, e a confirmação diz a consequência do modo em que se está. -->
    <div v-if="confirmingGiveUp" class="scrim" @click.self="confirmingGiveUp = false">
      <div class="confirm" role="dialog" aria-modal="true" aria-label="Desistir da partida">
        <h3>Desistir da partida?</h3>
        <p>{{ giveUpText }}</p>
        <div class="actions">
          <button class="btn btn-outline" type="button" @click="confirmingGiveUp = false">
            continuar
          </button>
          <button class="btn btn-cta" type="button" @click="giveUp">desistir</button>
        </div>
      </div>
    </div>

    <EndSheet
      v-if="resultOpen"
      :summary="summary"
      :scored="isScored"
      :is-record="isRecord"
      :level="level"
      :level-title="levelTitle"
      :xp-into-level="xpIntoLevel"
      :modes="modes"
      :current-mode="result ? result.game.mode : currentMode"
      :trail-outcome="trailOutcome"
      :duel-outcome="duelOutcome"
      :game-open="hasGame && !activeDuel"
      @again="playTraining"
      @play="trocarModo"
      @trail="$router.push('/trilha')"
      @duel="(id) => $router.push(`/duelos/${id}`)"
      @duels="$router.push('/duelos')"
      @close="closeResult"
    />
  </section>
</template>

<script>
import { reducedMotion } from '../lib/motion';
import { startLiveRefresh } from '../lib/live';
import { mapActions, mapGetters, mapState } from 'vuex';
import AppIcon from '../components/ui/AppIcon.vue';
import DuelCountdown from '../components/game/DuelCountdown.vue';
import HelpSheet from '../components/game/HelpSheet.vue';
import ModeSheet from '../components/game/ModeSheet.vue';
import EndSheet from '../components/game/EndSheet.vue';
import GameTile from '../components/game/GameTile.vue';
import StatusLine from '../components/game/StatusLine.vue';
import TutorialSheet, { tutorialJaVisto } from '../components/game/TutorialSheet.vue';
import VirtualKeyboard from '../components/game/VirtualKeyboard.vue';

export default {
  name: 'GameView',
  components: {
    AppIcon, DuelCountdown, EndSheet, GameTile, HelpSheet,
    ModeSheet, StatusLine, TutorialSheet, VirtualKeyboard,
  },
  data() {
    return {
      // Revalidação do duelo enquanto a folha de fim espera os outros.
      duelWatch: null,
      hitKey: null,
      showingGo: false,
      finishing: false,
      endGrace: 0,
      feedback: null,
      helpOpen: false,
      modesOpen: false,
      confirmingGiveUp: false,
      tutorialOpen: false,
      // O tutorial só grava a preferência quando abriu sozinho. Rever pelo botão da ajuda
      // não pode mexer numa escolha que o jogador já deu.
      tutorialAuto: false,
    };
  },
  computed: {
    ...mapState(['message', 'loading', 'modes', 'ready', 'resultToShow']),
    ...mapGetters({ game: 'activeGame' }),
    ...mapGetters(['hasGame', 'activeDuel', 'result', 'resultOpen', 'summary', 'isOver', 'isScored', 'tiles', 'worth', 'canSubmit', 'revealedCount', 'level', 'levelTitle', 'xpIntoLevel', 'stats', 'currentMode', 'trainingMode', 'trail', 'dockByPosition', 'keyboard', 'gamePhase', 'trailOutcome', 'gameDuel', 'duelOutcome', 'secondsLeft', 'duelLimit', 'countdown', 'preferences']),
    contextLabel() {
      if (this.gamePhase) return `trilha · fase ${this.gamePhase}`;
      if (this.gameDuel) return 'duel';
      if (!this.currentModeKey) return 'partida';
      return this.currentModeKey.key === 'classic-daily' ? 'chain do dia' : this.currentModeKey.name;
    },
    endLabel() {
      if (this.gameDuel) return 'sua vez no duelo acabou';
      if (this.gamePhase) return `fase ${this.gamePhase} encerrada`;
      return this.isScored ? 'a chain de hoje já foi' : 'treino encerrado';
    },
    /** Um traço por palavra a adivinhar: verde o que caiu, gradiente a corrente. */
    steps() {
      if (!this.game) return [];
      return this.game.words.map((w) => {
        if (w.solved === true) return 'feito';
        if (w.solved === false) return 'perdido';
        return w.position === this.game.current_position && !this.isOver ? 'agora' : '';
      });
    },
    resolved() {
      return this.game ? this.game.words.filter((w) => w.solved !== null).length : 0;
    },
    /** Se há partida aberta esperando letra. É o que decide o teclado. */
    typing() {
      return !!this.game && !this.isOver && !this.atStart;
    },
    /** Enquanto a largada corre, a tela inteira fica coberta. */
    atStart() {
      return !!this.gameDuel && (this.countdown > 0 || this.showingGo);
    },
    /** O "JÁ!" é o zero; sem ele a contagem terminaria em 1. */
    segundosDaLargada() {
      return this.showingGo ? 0 : this.countdown;
    },
    pressed() {
      return !this.atStart && this.secondsLeft !== null && this.secondsLeft <= 15;
    },
    timePct() {
      const limit = this.duelLimit;
      return Math.max(0, Math.min(100, ((this.secondsLeft ?? limit) / limit) * 100));
    },
    currentModeKey() {
      return this.modes.find((m) => m.key === this.currentMode) || null;
    },
    giveUpText() {
      if (this.gameDuel) return 'No duelo, desistir conta como jogado — e perde.';
      if (this.gamePhase) return 'Na trilha, desistir gasta um elo, como perder a fase.';
      if (this.isScored) return 'A chain de hoje é uma só: desistir encerra a sua partida do dia.';
      return 'A partida é encerrada e não pontua.';
    },
    /** O relógio em `m:ss`, para não obrigar ninguém a converter 74 segundos. */
    clock() {
      const total = Math.max(0, this.secondsLeft ?? 0);
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    },
    previousWord() {
      if (!this.game) return '';
      const pos = this.game.current_position;
      const anterior = this.game.words.find((w) => w.position === pos - 1);
      return (anterior?.word || this.game.anchor).toUpperCase();
    },
    currentHint() {
      if (!this.game) return null;
      const pos = this.game.current_position;
      const anterior = this.game.words.find((w) => w.position === pos - 1);
      return anterior ? anterior.definition : this.game.anchor_definition;
    },
    feedbackClass() {
      return this.feedback ? `is-${this.feedback}` : '';
    },
    isRecord() {
      return this.summary ? this.summary.score > 0 && this.summary.score >= this.stats.best_score : false;
    },
    currentPosition() {
      return this.game ? this.game.current_position : 0;
    },
  },
  watch: {
    /**
     * **A regra da tela, e só ela:** com partida em andamento, joga-se; sem ela, escolhe-se
     * o modo. O resultado é sempre folha por cima, nunca o estado base — foi o que fazia o
     * mesmo gesto ("Jogar") abrir ora a folha de modos, ora o resultado da partida passada.
     * Espera o `init`: decidir antes da reidratação abre a folha por cima da partida.
     */
    ready: { immediate: true, handler(pronto) { if (pronto) this.applyBaseState(); } },
    /**
     * Folha de fim esperando os outros do duelo: revalida até o servidor fechar. Sem isto
     * ela dizia "esperar quem falta jogar" para sempre, mesmo com todos já tendo jogado.
     */
    duelOutcome: {
      immediate: true,
      handler(outcome) {
        const waiting = Boolean(outcome && !outcome.closed);
        if (waiting && !this.duelWatch) {
          this.duelWatch = startLiveRefresh(() => this.$store.dispatch('refreshDuelOutcome'), { seconds: 10 });
        } else if (!waiting && this.duelWatch) {
          this.duelWatch.stop();
          this.duelWatch = null;
        }
      },
    },
    /** Partida encerrada: o fecho é overlay, e quem o abre é o flag — nunca a base. */
    resultOpen(open) {
      if (!open && !this.hasGame) this.modesOpen = true;
    },
    // A chain rola; a palavra que se está resolvendo tem de continuar à vista quando a
    // lista cresce acima dela.
    currentPosition() {
      this.scrollToActive();
    },
  },
  async mounted() {
    // O relógio do duelo desconta localmente entre uma jogada e outra; a resposta do
    // servidor reescreve o número a cada palpite, e zerar aqui não encerra nada.
    this.relogioDoDuelo = setInterval(() => {
      if (!this.gameDuel || this.isOver) return;
      // Primeiro a largada; só quando ela zera o relógio da partida começa a descer.
      if (this.countdown > 0) {
        this.$store.commit('tickCountdown');
        // Zerou: o "JÁ!" fica meio segundo em cena antes de a chain aparecer.
        if (this.countdown === 0) this.blinkStart();
        return;
      }

      this.$store.commit('tickClock');

      // Zerou o relógio: o servidor fecha a partida. A carência existe porque o relógio
      // daqui é desenho e pode estar adiantado — cobrar o fim antes da hora tiraria
      // segundos de quem ainda os tinha.
      if (this.secondsLeft === 0 && !this.finishing) {
        this.endGrace += 1;
        if (this.endGrace >= 2) {
          this.finishing = true;
          this.$store.dispatch('settleDuelTime').finally(() => { this.finishing = false; });
        }
      } else if (this.secondsLeft > 0) {
        this.endGrace = 0;
      }
    }, 1000);
    window.addEventListener('keydown', this.onKeydown);
    window.addEventListener('sentido:help', this.openHelp);
    window.addEventListener('sentido:tab-repeat', this.openModes);
    // Primeira visita abre o tutorial sozinho. Quem já passou por ele, não vê de novo.
    if (!tutorialJaVisto()) {
      this.tutorialAuto = true;
      this.tutorialOpen = true;
    }
    // O fecho da fase e o aviso de desistência dizem o preço da trilha, e os dois leem o
    // mapa: quem entrou direto nesta rota ainda não o tem carregado.
    if (!this.trail) this.loadTrail();

    // A base é decidida no `watch` de `pronto`, que roda também na montagem: aplicar aqui
    // de novo desfazia o que ele acabou de decidir.
  },
  beforeUnmount() {
    clearInterval(this.relogioDoDuelo);
    this.duelWatch?.stop();
    window.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('sentido:help', this.openHelp);
    window.removeEventListener('sentido:tab-repeat', this.openModes);
  },
  methods: {
    ...mapActions(['switchGame', 'loadTrail', 'press']),
    /** Toque na casa: a próxima letra cai ali, contando dentro do trecho palpitável. */
    pickTile(index) {
      this.$store.commit('setCaret', index - this.revealedCount);
    },
    chooseMode(key) {
      this.modesOpen = false;
      return this.trocarModo(key);
    },
    /**
     * Fechar o fecho não muda a base — ele é overlay, e só o flag o comanda. A exceção é a
     * trilha: a casa dela é o mapa, e cair na folha de seleção era expulsar do modo quem
     * só terminou uma fase.
     */
    closeResult() {
      this.$store.commit('setResultToShow', false);
      if (this.result?.game?.trail_phase && !this.hasGame) this.$router.push('/trilha');
    },
    /** Reabre o fecho guardado, sem tocar na partida. */
    openStoredResult() {
      this.modesOpen = false;
      this.$store.commit('setResultToShow', true);
    },
    /**
     * **A base, e só ela:** partida ativa manda; sem partida ativa, folha de modos. O
     * resultado nunca entra nesta conta — ele é overlay, e o flag da store decide se está
     * aberto.
     */
    applyBaseState() {
      this.modesOpen = !this.hasGame && !this.resultOpen;
    },
    /** Porta única para a folha de modos: pela aba ou pelo cartão de contexto. */
    openModes() {
      this.$store.commit('setResultToShow', false);
      this.modesOpen = true;
    },
    askGiveUp() {
      this.confirmingGiveUp = true;
    },
    async giveUp() {
      this.confirmingGiveUp = false;
      await this.$store.dispatch('forfeitGame');
    },
    replayTutorial() {
      this.helpOpen = false;
      this.tutorialAuto = false;
      this.tutorialOpen = true;
    },
    closeTutorial() {
      this.tutorialOpen = false;
      this.tutorialAuto = false;
    },
    async onPress(key) {
      this.hitKey = key;
      setTimeout(() => { this.hitKey = null; }, 110);

      if (key === 'ENTER') {
        const result = await this.press('ENTER');
        if (!result) return;
        // Recusa tem aviso próprio: nada foi cobrado, então nada de tremor vermelho.
        if (result.rejected) this.blink('recusado');
        else this.blink(result.correct ? 'certo' : 'errado');
        return;
      }
      this.press(key);
    },
    /** Traz o bloco da palavra corrente para dentro da parte visível da chain. */
    scrollToActive() {
      this.$nextTick(() => {
        const ativa = this.$el?.querySelector('.active');
        if (!ativa) return;
        const suave = !reducedMotion();
        ativa.scrollIntoView({ block: 'nearest', behavior: suave ? 'smooth' : 'auto' });
      });
    },
    blinkStart() {
      this.showingGo = true;
      setTimeout(() => { this.showingGo = false; }, 600);
    },
    blink(kind) {
      this.feedback = kind;
      setTimeout(() => { this.feedback = null; }, kind === 'certo' ? 620 : 460);
    },
    onKeydown(event) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      let key = null;
      if (event.key === 'Enter') key = 'ENTER';
      else if (event.key === 'Backspace') key = 'BACK';
      else if (/^[a-zA-Z]$/.test(event.key)) key = event.key.toUpperCase();
      if (!key) return;
      event.preventDefault();
      this.onPress(key);
    },
    playTraining() {
      if (!this.trainingMode) return;
      this.$store.commit('setResultToShow', false);
      return this.switchGame({ kind: 'mode', value: this.trainingMode.key });
    },
    /**
     * Trocar de partida passa pela store, que é onde mora o aviso de desistência: assim
     * vale igual aqui, no cartão de Hoje, no nó da trilha e na lista de duelos.
     */
    async trocarModo(mode) {
      await this.switchGame({ kind: 'mode', value: mode });
      this.applyBaseState();
    },
    openHelp() {
      this.helpOpen = true;
    },
  },
};
</script>

<style scoped>
.game { padding: 0 18px; gap: 12px; }

/* O teclado sai do respiro lateral da tela: ele é uma faixa, como o teclado do sistema,
   e as teclas das pontas cresciam menos que as do meio por causa dos 18px de cada lado. */
.keyboard { margin: 0 -18px; }

/* ── contexto ──────────────────────────────────────────────────────────── */
.context {
  flex: none;
  border-radius: var(--r-card);
  background: var(--veil-1);
  border: 1px solid var(--line);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  min-width: 0;
}

.context-top,
.context-bottom {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}

.exit,
.give-up {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 0;
  font: 700 11px/1 var(--f-label);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-decoration: none;
  color: var(--blue-text);
}

.help {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-3);
}

.help:hover { color: var(--active); }

.give-up { color: var(--text-3); }
.give-up:hover { color: var(--error); }

.steps { flex: 1; display: flex; gap: 4px; min-width: 80px; }

.step {
  flex: 1;
  height: 4px;
  border-radius: var(--r-pill);
  background: var(--veil-3);
}

.step.done { background: var(--ok); }
.step.now { background: var(--brand-gradient); }
.step.missed { background: var(--error); }

.count { font-size: 11px; color: var(--text-3); }

.clock {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--text-2);
  font-size: 13px;
}

.clock.pressed { color: var(--error); }

.time-bar {
  flex: 1 1 60px;
  min-width: 24px;
  height: 4px;
  border-radius: var(--r-pill);
  background: var(--veil-3);
  overflow: hidden;
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: var(--r-pill);
  background: var(--active);
  transition: width 1s linear;
}

.pressed .bar-fill { background: var(--error); }

/* ── dica ──────────────────────────────────────────────────────────────── */
.hint {
  flex: none;
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 12px 14px;
  border-radius: var(--r-card);
  background: var(--veil-2);
  border: 1px solid var(--line-2);
}

.hint > :deep(.ic) { color: var(--pink-text); margin-top: 1px; }
.hint p { margin: 5px 0 0; font: 600 13px/1.35 var(--f-body); color: var(--text-2); }

/* ── a chain ───────────────────────────────────────────────────────────────
   Ela rola, e seus filhos não encolhem: a lista cresce com o jogo, e a tela do
   celular não cresce junto. */
.chain {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-padding-block: 8px;
}

.chain > * { flex: none; }

/* Diretiva 05: uma linha por palavra resolvida. */
.link {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 9px;
  padding: 11px 13px;
  border-radius: var(--r-button);
  animation: link-in 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes link-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: none; }
}

.link .word {
  min-width: 0;
  font: 700 16px/1 var(--f-title);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text);
}

.link .dot { font-size: 13px; }

.link-anchor {
  background: var(--active-veil);
  border: 1px solid var(--active-line);
  color: var(--active);
}

.link-anchor .word { font-size: 17px; }

.link-ok {
  background: var(--ok-veil);
  border: 1px solid var(--ok-line);
  color: var(--ok);
}

.link-error {
  background: var(--error-veil);
  border: 1px solid var(--error-line);
  color: var(--error);
}

.link-error .word { text-decoration: line-through; text-decoration-thickness: 2px; }

/* ── a palavra corrente ────────────────────────────────────────────────── */
.active {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 0 2px;
  scroll-margin-block: 12px;
}

.active-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.active-top .lbl { display: inline-flex; align-items: center; gap: 7px; min-width: 0; }

/* Diretiva 07: o ponto vivo. Ele muda de cor quando começa a cair. */
.value {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: none;
  padding: 6px 11px;
  border-radius: 9px;
  background: var(--amber-veil);
  border: 1px solid var(--amber-line);
  color: var(--amber);
  font: 700 12px/1 var(--f-label);
}

.value.falling { background: var(--error-veil); border-color: var(--error-line); color: var(--error-light); }

.boxes { display: flex; gap: 6px; justify-content: center; }

/* Diretiva 06: uma animação por evento. */
.boxes.is-wrong { animation: shake 0.42s cubic-bezier(0.36, 0.07, 0.19, 0.97); }
.boxes.is-rejected { animation: refuse 0.4s ease-out; }

@keyframes shake {
  10%, 90% { transform: translateX(-2px); }
  20%, 80% { transform: translateX(3px); }
  30%, 50%, 70% { transform: translateX(-6px); }
  40%, 60% { transform: translateX(6px); }
}

@keyframes refuse {
  0%, 100% { transform: none; }
  35% { transform: translateY(-5px); }
  70% { transform: translateY(2px); }
}

/* ── palavra futura (diretiva 03) ──────────────────────────────────────── */
.future {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 3px;
  color: var(--text-3);
}

.future .question { font: 700 14px/1 var(--f-title); }
.future .dots { flex: 1; display: flex; gap: 4px; }

.future .dots i {
  width: 4px;
  height: 4px;
  border-radius: var(--r-pill);
  background: var(--veil-3);
}

.future .letters { font: 600 11px/1 var(--f-body); }

.loading { text-align: center; padding: 28px 0; }

/* ── fim ───────────────────────────────────────────────────────────────── */
.end {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 14px 0 6px;
}

/* Estado sem partida: é convite, não erro. */
.no-game { display: grid; justify-items: center; gap: 14px; padding: 40px 24px; text-align: center; }
.no-game p { margin: 0; font: 600 13.5px/1.5 var(--f-body); color: var(--text-3); max-width: 30ch; }
</style>
